/**
 * /conversions/scrub — conversion scrub / manual-review queue page (Feature #139).
 *
 * Declares a TanStack Router route and renders `ScrubWorkflowPage`. The
 * component is imported by the root-owned `router.tsx` via module augmentation
 * — agents never touch `router.tsx` themselves; the root wire-up cycle picks
 * this route up after the prod merge.
 *
 * Search params reserved for:
 *   ?tab=queue|audit
 *   ?status=pending|approved|rejected|all
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { ScrubWorkflowPage, isScrubTab } from "@/features/scrub-workflow/ScrubWorkflowPage";
import { isQueueFilter, type QueueFilter } from "@/features/scrub-workflow/api";

// ---------------------------------------------------------------------------
// Search params
// ---------------------------------------------------------------------------

type ScrubTab = "queue" | "audit";

interface ConversionsScrubSearch {
	tab?: ScrubTab;
	status?: QueueFilter;
}

export const conversionsScrubRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/conversions/scrub",
	validateSearch: (raw: Record<string, unknown>): ConversionsScrubSearch => {
		const out: ConversionsScrubSearch = {};
		if (isScrubTab(raw.tab)) out.tab = raw.tab;
		if (isQueueFilter(raw.status)) out.status = raw.status;
		return out;
	},
	component: ConversionsScrubRoute,
});

export default conversionsScrubRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + the live feature component.
// ---------------------------------------------------------------------------

export function ConversionsScrubRoute() {
	const { tab, status } = conversionsScrubRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<ScrubWorkflowPage initialTab={tab} initialStatus={status} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Conversions</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Scrub</li>
			</ol>
		</nav>
	);
}
